import { z } from 'zod';
import { createSchema } from 'zod-openapi';

// 🟢 Login Request Schema
export const LoginRequestSchema = z.object({
	email: z.string().email().meta({
		title: 'Email',
		description: 'User email address',
		example: 'slajara@gainkode.com',
	}),
	password: z.string().min(6).meta({
		title: 'Password',
		description: 'User password',
		example: 'Test1234*',
		minLength: 6,
	}),
});

// 🟠 Login Response Schema (Supabase)
export const LoginResponseSchema = z.object({
	access_token: z.string().meta({
		title: 'Access Token',
		description: 'JWT access token from Supabase',
		example: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
	}),
	refresh_token: z.string().meta({
		title: 'Refresh Token',
		description: 'Refresh token from Supabase',
		example: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
	}),
	expires_in: z.number().meta({
		title: 'Expires In',
		description: 'Token expiration time in seconds',
		example: 3600,
	}),
	expires_at: z.number().meta({
		title: 'Expires At',
		description: 'Token expiration timestamp',
		example: 1640000000,
	}),
	user: z
		.object({
			id: z.string().meta({ title: 'User ID (Supabase UUID)', example: '123e4567-e89b-12d3-a456-426614174000' }),
			email: z.string().meta({ title: 'Email', example: 'admin@test.com' }),
			user_metadata: z
				.object({
					first_name: z.string().optional().meta({ title: 'First Name', example: 'John' }),
					last_name: z.string().optional().meta({ title: 'Last Name', example: 'Doe' }),
					phone: z.string().optional().meta({ title: 'Phone', example: '+34600000000' }),
				})
				.optional()
				.meta({ title: 'User Metadata', example: {} }),
		})
		.describe('Información del usuario desde Supabase'),
});

// 🟠 Logout Request Schema
export const LogoutRequestSchema = z.object({
	refresh_token: z.string().meta({
		title: 'Refresh Token',
		description: 'Token de actualización para invalidar la sesión',
		example: 'eyJhbGciOiJIUzUxMiIsInR5cCI6IkpXVCJ9...',
	}),
	access_token: z.string().meta({
		title: 'Access Token',
		description: 'Token de acceso requerido para autorización en el servicio externo',
		example: 'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...',
	}),
});

// 🟠 Logout Response Schema
export const LogoutResponseSchema = z.object({
	message: z.string().meta({
		title: 'Logout Message',
		description: 'Mensaje de salida',
		example: 'Logout exitoso',
	}),
});

// 🟠 JWT Payload Schema
export const JWTPayload = z.object({
	userId: z.string().meta({ title: 'User ID', example: '123' }),
	email: z.string().meta({ title: 'Email', example: 'admin@test.com' }),
	role: z.string().meta({ title: 'Role', example: 'admin' }),
	roleId: z.number().meta({ title: 'Role ID', example: 2 }),
});

// ✅ OpenAPI Schemas (using `createSchema()` for all)
export const { schema: LoginRequestOpenAPI } = createSchema(LoginRequestSchema);
export const { schema: LoginResponseOpenAPI } = createSchema(LoginResponseSchema);
export const { schema: LogoutRequestOpenAPI } = createSchema(LogoutRequestSchema);
export const { schema: LogoutResponseOpenAPI } = createSchema(LogoutResponseSchema);
export const { schema: JWTPayloadOpenAPI } = createSchema(JWTPayload);

// ✅ EXPORT DTOs
export type LoginRequestSchemaDTO = z.infer<typeof LoginRequestSchema>;
export type LoginResponseSchemaDTO = z.infer<typeof LoginResponseSchema>;
export type LogoutRequestSchemaDTO = z.infer<typeof LogoutRequestSchema>;
export type LogoutResponseSchemaDTO = z.infer<typeof LogoutResponseSchema>;
export type JWTPayloadDTO = z.infer<typeof JWTPayload>;

// Reset Password Request Schema
export const ResetPasswordRequestSchema = z.object({
	email: z.string().email().describe('User email'),
});

export const ResetPasswordResponseSchema = z.object({
	message: z.string().describe('Response message'),
});

// The two emails that carry a set-password link (24/09). The link points at OUR
// screen with the `token_hash`, and the token is only spent when the form is
// sent — never by opening the page. Corporate mail scanners (Safe Links,
// Proofpoint) open every link the moment it arrives, and Supabase's own
// `/verify` link spends the token on that first GET: the invitation was dead
// before the person ever saw it.
export const EMAIL_LINK_TYPES = ['invite', 'recovery'] as const;
export type EmailLinkType = (typeof EMAIL_LINK_TYPES)[number];
/** Proof a set-password screen sends back: the link's token_hash, or a session. */
export type EmailLinkProof = { accessToken?: string; tokenHash?: string; type?: EmailLinkType };

// Which proof of the link came in: the `token_hash` of the current emails, or
// the `access_token` fragment that links sent before 24/09 still carry (and that
// the privacy step hands back, once the token_hash has been spent).
const emailLinkFields = {
	accessToken: z.string().min(1).optional().describe('Session token from an older link (URL fragment)'),
	tokenHash: z.string().min(1).optional().describe('token_hash carried by the email link'),
	type: z.enum(EMAIL_LINK_TYPES).optional().describe('Link type, required with tokenHash'),
};
const hasEmailLinkProof = (body: { accessToken?: string; tokenHash?: string; type?: string }) =>
	!!body.accessToken || (!!body.tokenHash && !!body.type);
const EMAIL_LINK_PROOF_MESSAGE = 'accessToken, or tokenHash with type, is required';

// Update Password With Token Request Schema
export const UpdatePasswordWithTokenRequestSchema = z
	.object({
		...emailLinkFields,
		newPassword: z.string().min(8).describe('New password'),
	})
	.refine(hasEmailLinkProof, { message: EMAIL_LINK_PROOF_MESSAGE });

export const UpdatePasswordWithTokenResponseSchema = z.object({
	message: z.string().describe('Response message'),
});

// Activate Account Request Schema — same shape as the reset: the invitation link
// names the account, and the person chooses the password that unlocks it. The
// response is the login response (the session opens right there), or
// `{ privacy_required, activation_token }` when a migrated candidate still has
// to tick the privacy box — the token_hash is spent by then, so the screen
// retries with that token instead.
export const ActivateAccountRequestSchema = z
	.object({
		...emailLinkFields,
		newPassword: z.string().min(8).describe('Password chosen by the invited person'),
		privacyAccepted: z
			.boolean()
			.optional()
			.describe('PDPL privacy acceptance; required for a migrated candidate who has not accepted it yet'),
	})
	.refine(hasEmailLinkProof, { message: EMAIL_LINK_PROOF_MESSAGE });

// Resend Activation Request Schema — an invited person whose link expired asks
// for a new one. Public and enumeration-safe: same answer whether or not the
// address has an account.
export const ResendActivationRequestSchema = z.object({
	email: z.string().email().describe('Email the invitation was sent to'),
});

// Verify Email Request Schema
export const VerifyEmailRequestSchema = z.object({
	token: z.string().describe('Email verification token from Supabase'),
	type: z.string().optional().describe('Token type (signup, email_change, etc.)'),
});

export const VerifyEmailResponseSchema = z.object({
	message: z.string().describe('Success message'),
	verified: z.boolean().describe('Whether email was verified'),
});

// Resend Verification Email Request Schema
export const ResendVerificationRequestSchema = z.object({
	email: z.string().email().describe('Email to resend verification to'),
});

export const ResendVerificationResponseSchema = z.object({
	message: z.string().describe('Success message'),
});

// 🟢 OpenAPI Schemas
export const { schema: ResetPasswordRequestOpenAPI } = createSchema(ResetPasswordRequestSchema);
export const { schema: ResetPasswordResponseOpenAPI } = createSchema(ResetPasswordResponseSchema);
export const { schema: UpdatePasswordWithTokenRequestOpenAPI } = createSchema(UpdatePasswordWithTokenRequestSchema);
export const { schema: UpdatePasswordWithTokenResponseOpenAPI } = createSchema(UpdatePasswordWithTokenResponseSchema);
export const { schema: ActivateAccountRequestOpenAPI } = createSchema(ActivateAccountRequestSchema);
export const { schema: ResendActivationRequestOpenAPI } = createSchema(ResendActivationRequestSchema);
export const { schema: VerifyEmailRequestOpenAPI } = createSchema(VerifyEmailRequestSchema);
export const { schema: VerifyEmailResponseOpenAPI } = createSchema(VerifyEmailResponseSchema);
export const { schema: ResendVerificationRequestOpenAPI } = createSchema(ResendVerificationRequestSchema);
export const { schema: ResendVerificationResponseOpenAPI } = createSchema(ResendVerificationResponseSchema);

// Type definitions
export type ResetPasswordRequestSchemaDTO = z.infer<typeof ResetPasswordRequestSchema>;
export type ResetPasswordResponseSchemaDTO = z.infer<typeof ResetPasswordResponseSchema>;
export type UpdatePasswordWithTokenRequestSchemaDTO = z.infer<typeof UpdatePasswordWithTokenRequestSchema>;
export type UpdatePasswordWithTokenResponseSchemaDTO = z.infer<typeof UpdatePasswordWithTokenResponseSchema>;
export type ActivateAccountRequestSchemaDTO = z.infer<typeof ActivateAccountRequestSchema>;
export type ResendActivationRequestSchemaDTO = z.infer<typeof ResendActivationRequestSchema>;
export type VerifyEmailRequestSchemaDTO = z.infer<typeof VerifyEmailRequestSchema>;
export type VerifyEmailResponseSchemaDTO = z.infer<typeof VerifyEmailResponseSchema>;
export type ResendVerificationRequestSchemaDTO = z.infer<typeof ResendVerificationRequestSchema>;
export type ResendVerificationResponseSchemaDTO = z.infer<typeof ResendVerificationResponseSchema>;
